/**
 * Custom sleeves cost estimator.
 *
 * Quotes a sachet count against the published rates: a per-sachet price that
 * depends on format, a one-off customisation fee, and volume discounts at two
 * thresholds. Every rate comes from data attributes on the element, so the
 * section settings stay the single source of truth — nothing is hardcoded here.
 *
 * It is an estimate, not a checkout. The figure it shows is the same one the
 * enquiry form sends, so the customer and the DrinkAid inbox see one number.
 *
 * Money is handled in cents and only divided for display.
 */
class DaSleevesEstimator extends HTMLElement {
  connectedCallback() {
    this.formatChips = Array.from(this.querySelectorAll('[data-format]'));
    this.countInput = this.querySelector('[data-count]');
    if (!this.countInput) return;

    this.rates = {
      capsules: Math.round(Number(this.dataset.priceCapsules || 0) * 100),
      gummies: Math.round(Number(this.dataset.priceGummies || 0) * 100),
    };
    this.fee = Math.round(Number(this.dataset.fee || 0) * 100);
    this.minimum = Number(this.dataset.minimum || 0);
    this.tier1 = { at: Number(this.dataset.tier1At || 0), pct: Number(this.dataset.tier1Pct || 0) };
    this.tier2 = { at: Number(this.dataset.tier2At || 0), pct: Number(this.dataset.tier2Pct || 0) };
    this.currency = this.dataset.currency || 'S$';

    this.formatChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.formatChips.forEach((c) => c.setAttribute('aria-checked', String(c === chip)));
        this.#render();
      });
    });
    this.countInput.addEventListener('input', () => this.#render());

    this.#render();
  }

  get #format() {
    const active = this.formatChips.find((c) => c.getAttribute('aria-checked') === 'true');
    return active?.dataset.format ?? 'capsules';
  }

  /** A mix is quoted at the midpoint of the two rates — stated in the UI. */
  #rateFor(format) {
    if (format === 'mix') return Math.round((this.rates.capsules + this.rates.gummies) / 2);
    return this.rates[format] ?? this.rates.capsules;
  }

  #money(cents) {
    return this.currency + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  #render() {
    const format = this.#format;
    const count = Math.max(0, Math.floor(Number(this.countInput.value) || 0));
    const rate = this.#rateFor(format);

    const sachets = rate * count;

    // Thresholds are checked high-to-low so the better rate wins at the boundary.
    let pct = 0;
    if (this.tier2.at > 0 && count >= this.tier2.at) pct = this.tier2.pct;
    else if (this.tier1.at > 0 && count >= this.tier1.at) pct = this.tier1.pct;

    // The discount applies to the sachets, not the customisation fee.
    const discount = Math.round((sachets * pct) / 100);
    const total = sachets - discount + (count > 0 ? this.fee : 0);

    this.#set('[data-out-rate]', `${this.#money(rate)} / sachet`);
    this.#set('[data-out-sachets]', this.#money(sachets));
    this.#set('[data-out-fee]', this.#money(count > 0 ? this.fee : 0));
    this.#set('[data-out-total]', this.#money(total));
    this.#set('[data-out-count]', String(count));

    const discountRow = this.querySelector('[data-discount-row]');
    if (discountRow) discountRow.hidden = discount <= 0;
    this.#set('[data-out-discount-label]', `Volume discount (${pct}%)`);
    this.#set('[data-out-discount]', `− ${this.#money(discount)}`);

    const perGuest = this.querySelector('[data-out-per-sachet]');
    if (perGuest) {
      perGuest.textContent = count > 0 ? `${this.#money(Math.round(total / count))} all-in per sachet` : '';
      perGuest.hidden = count <= 0;
    }

    // Below the minimum the quote is not orderable, so say so rather than
    // showing a total the team would have to decline.
    const warn = this.querySelector('[data-min-warning]');
    if (warn) {
      const below = count > 0 && this.minimum > 0 && count < this.minimum;
      warn.hidden = !below;
    }

    // Keep the enquiry form in step, so the customer sends the number they saw.
    const formCount = document.querySelector('[data-sync-count]');
    if (formCount instanceof HTMLInputElement && count > 0) formCount.value = String(count);

    const formFormat = document.querySelector('[data-sync-format]');
    if (formFormat instanceof HTMLSelectElement) {
      const match = Array.from(formFormat.options).find((o) => o.value.toLowerCase().includes(format));
      if (match) formFormat.value = match.value;
    }
  }

  #set(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }
}

if (!customElements.get('da-sleeves-estimator')) {
  customElements.define('da-sleeves-estimator', DaSleevesEstimator);
}
